import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../../api';
import type { GitProvider, RemoteRepository } from '../../api';
import { getSavedGitCredential, saveGitCredential } from '../../utils/gitCredentials';

/** One repository the connected account can read, as the selector lists it. */
export interface RepositoryItem {
  fullName: string;
  branches: string[];
  /** The branch the provider reports as the repository's default, when known. */
  defaultBranch?: string;
  updated: string;
  isPrivate: boolean;
  /** Whether the provider listing recognised Magento code in it. Shown as a
   * hint on Magento onboarding; never used to filter the list. */
  magento: boolean;
}

/**
 * RepositorySource is what the repository list needs from wherever the
 * repositories come from. useGitRepositories (onboarding) and
 * useProjectRepositories (an existing project) both provide it, so the
 * same selector is used before and after a project exists.
 */
export interface RepositorySource {
  provider: GitProvider;
  repositories: RepositoryItem[];
  loadingRepos: boolean;
  repoListError: string | null;
  reload: () => void;
  /** Reconnects the provider after a credential failure; omitted where the
   * page manages repository access elsewhere. */
  handleConnectProvider?: (provider: GitProvider) => void;
}

/** How Commerce Ops reads the listed repositories: the organization's
 * installed GitHub App, or a token entered in this page. */
export type GitConnectionKind = 'github_app' | 'token';

export const gitProviderNames: Record<GitProvider, string> = { github: 'GitHub', gitlab: 'GitLab', bitbucket: 'Bitbucket' };
export const gitProviderHosts: Record<GitProvider, string> = { github: 'github.com', gitlab: 'gitlab.com', bitbucket: 'bitbucket.org' };

/** The HTTPS clone URL of a listed repository — the form a deployment's
 * checkout authenticates with a token or GitHub App installation token. */
export function httpsCloneUrl(provider: GitProvider, fullName: string): string {
  return `https://${gitProviderHosts[provider]}/${fullName}.git`;
}

/** The SSH clone URL of a listed repository (what Magento onboarding has
 * always recorded). */
export function sshCloneUrl(provider: GitProvider, fullName: string): string {
  return `git@${gitProviderHosts[provider]}:${fullName}.git`;
}

export function toItems(repos: RemoteRepository[] | undefined): RepositoryItem[] {
  return (repos ?? []).map((r) => ({
    fullName: r.full_name,
    isPrivate: r.private !== false,
    updated: r.updated || 'recently',
    branches: r.branches && r.branches.length > 0 ? r.branches : r.default_branch ? [r.default_branch] : [],
    defaultBranch: r.default_branch || undefined,
    magento: Boolean(r.magento),
  }));
}

function fromSaved<T>(pick: (cred: ReturnType<typeof getSavedGitCredential>) => T): Record<GitProvider, T> {
  return {
    github: pick(getSavedGitCredential('github')),
    gitlab: pick(getSavedGitCredential('gitlab')),
    bitbucket: pick(getSavedGitCredential('bitbucket')),
  };
}

export interface UseGitRepositoriesOptions {
  /**
   * Called with every freshly loaded list. `replace` is true when the list
   * comes from a connection made just now, false when it is a (re)load.
   * Magento onboarding uses it to preselect a repository; WordPress
   * onboarding does not preselect anything.
   */
  onRepositoriesLoaded?: (repositories: RepositoryItem[], replace: boolean) => void;
  /** Called just before the page is left for GitHub's App registration, so
   * the page can keep what was entered. */
  onBeforeRedirect?: () => void;
}

/**
 * useGitRepositories is the repository-listing half of project onboarding:
 * which Git provider is chosen, whether it is connected (the
 * organization's GitHub App, or a token entered in this visit), the
 * repositories that connection can read, and the connect dialog.
 *
 * Tokens stay in memory for this page load only (utils/gitCredentials);
 * they reach the server when the project is created, where they are
 * stored encrypted as the project's Git connection.
 */
export function useGitRepositories({ onRepositoriesLoaded, onBeforeRedirect }: UseGitRepositoriesOptions = {}) {
  const [provider, setProvider] = useState<GitProvider>('github');
  const [providerAccounts, setProviderAccounts] = useState<Record<GitProvider, string>>(() => fromSaved((c) => c?.account || ''));
  const [providerTokens, setProviderTokens] = useState<Record<GitProvider, string>>(() => fromSaved((c) => c?.token || ''));
  const [connectedProviders, setConnectedProviders] = useState<Record<GitProvider, boolean>>(() => fromSaved((c) => Boolean(c && c.token)));
  // The organization's GitHub App lists GitHub repositories without any
  // token in the browser.
  const [githubAppInstalled, setGithubAppInstalled] = useState(false);
  const [repositories, setRepositories] = useState<RepositoryItem[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoListError, setRepoListError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Connect dialog state
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [modalProvider, setModalProvider] = useState<GitProvider>('github');
  const [modalAccount, setModalAccount] = useState('');
  const [modalToken, setModalToken] = useState('');
  const [modalConnecting, setModalConnecting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [githubConnectTab, setGithubConnectTab] = useState<'app' | 'token' | 'manifest'>('app');
  const [ghAppId, setGhAppId] = useState('');
  const [ghAppName, setGhAppName] = useState('');
  const [ghClientId, setGhClientId] = useState('');
  const [ghClientSecret, setGhClientSecret] = useState('');
  const [ghWebhookSecret, setGhWebhookSecret] = useState('');
  const [ghPrivateKey, setGhPrivateKey] = useState('');
  const [ghInstallationId, setGhInstallationId] = useState('');
  const [ghAccount, setGhAccount] = useState('');
  const [ghOrgName, setGhOrgName] = useState('');

  const currentAccount = providerAccounts[provider];
  const currentToken = providerTokens[provider];

  // The latest callbacks, read through a ref so a page re-rendering does
  // not reload the list.
  const callbacks = useRef({ onRepositoriesLoaded, onBeforeRedirect });
  callbacks.current = { onRepositoriesLoaded, onBeforeRedirect };

  useEffect(() => {
    let cancelled = false;
    async function loadRepos() {
      setLoadingRepos(true);
      setRepoListError(null);
      try {
        // For GitHub, the GitHub App integration comes first (no token needed).
        if (provider === 'github') {
          let installed = false;
          try {
            const appStatus = await api.getGitHubApp();
            installed = Boolean(appStatus.configured && appStatus.installed);
          } catch {
            // Status unknown: treat as not installed and fall back to a token.
          }
          if (cancelled) return;
          setGithubAppInstalled(installed);
          if (installed) {
            try {
              const res = await api.listGitHubAppRepositories();
              if (!cancelled) {
                const mapped = toItems(res?.repositories);
                setRepositories(mapped);
                callbacks.current.onRepositoriesLoaded?.(mapped, false);
                setConnectedProviders((prev) => (prev.github ? prev : { ...prev, github: true }));
              }
              return;
            } catch (err) {
              // The App is installed but cannot list (uninstalled on GitHub,
              // expired key): say so, unless a token entered here can list.
              if (!currentToken) {
                if (!cancelled) {
                  setRepositories([]);
                  setRepoListError(err instanceof Error ? err.message : 'The GitHub App could not list repositories');
                }
                return;
              }
            }
          }
        }

        if (!currentToken) {
          if (!cancelled) setRepositories([]);
          return;
        }

        const res = await api.listGitProviderRepositories({ provider, account: currentAccount, token: currentToken });
        if (!cancelled) {
          const mapped = toItems(res?.repositories);
          setRepositories(mapped);
          if (mapped.length > 0) {
            callbacks.current.onRepositoriesLoaded?.(mapped, false);
            setConnectedProviders((prev) => (prev[provider] ? prev : { ...prev, [provider]: true }));
          }
        }
      } catch (err) {
        if (!cancelled) {
          setRepositories([]);
          setRepoListError(err instanceof Error ? err.message : 'Failed to load repositories');
        }
      } finally {
        if (!cancelled) setLoadingRepos(false);
      }
    }
    loadRepos();
    return () => {
      cancelled = true;
    };
  }, [provider, currentAccount, currentToken, reloadKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  function handleConnectProvider(p: GitProvider) {
    setModalProvider(p);
    if (p === 'github') setGithubConnectTab('app');
    setModalAccount(providerAccounts[p] || '');
    setModalToken(providerTokens[p] || '');
    setModalError(null);
    setConnectModalOpen(true);
  }

  async function handleSaveProviderConnection(e: FormEvent) {
    e.preventDefault();
    setModalConnecting(true);
    setModalError(null);
    try {
      const trimmedAccount = modalAccount.trim();
      const trimmedToken = modalToken.trim();
      // Test credentials by listing repositories
      const res = await api.listGitProviderRepositories({ provider: modalProvider, account: trimmedAccount, token: trimmedToken });
      setConnectedProviders((prev) => ({ ...prev, [modalProvider]: true }));
      setProviderAccounts((prev) => ({ ...prev, [modalProvider]: trimmedAccount }));
      if (trimmedToken) setProviderTokens((prev) => ({ ...prev, [modalProvider]: trimmedToken }));
      saveGitCredential(modalProvider, { account: trimmedAccount, token: trimmedToken, connectedAt: new Date().toISOString() });
      const mapped = toItems(res.repositories);
      if (mapped.length > 0) {
        setRepositories(mapped);
        callbacks.current.onRepositoriesLoaded?.(mapped, true);
      }
      setConnectModalOpen(false);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to verify credentials');
    } finally {
      setModalConnecting(false);
    }
  }

  async function handleSaveManualGitHubApp(e: FormEvent) {
    e.preventDefault();
    setModalConnecting(true);
    setModalError(null);
    try {
      const parsedAppId = parseInt(ghAppId.trim(), 10);
      const parsedInstallationId = parseInt(ghInstallationId.trim(), 10);
      if (isNaN(parsedAppId)) throw new Error('Valid numerical App ID is required');
      if (isNaN(parsedInstallationId)) throw new Error('Valid numerical Installation ID is required');

      await api.saveManualGitHubApp({
        app_id: parsedAppId,
        app_name: ghAppName.trim() || undefined,
        client_id: ghClientId.trim() || undefined,
        client_secret: ghClientSecret.trim(),
        webhook_secret: ghWebhookSecret.trim(),
        private_key: ghPrivateKey.trim(),
        installation_id: parsedInstallationId,
        account: ghAccount.trim(),
      });

      const res = await api.listGitHubAppRepositories();
      const mapped = toItems(res?.repositories);
      setRepositories(mapped);
      callbacks.current.onRepositoriesLoaded?.(mapped, true);
      setGithubAppInstalled(true);
      setConnectedProviders((prev) => ({ ...prev, github: true }));
      setProviderAccounts((prev) => ({ ...prev, github: ghAccount.trim() }));
      setConnectModalOpen(false);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to connect GitHub App');
    } finally {
      setModalConnecting(false);
    }
  }

  async function handleStartManifestFlowFromModal() {
    setModalConnecting(true);
    setModalError(null);
    try {
      const res = await api.getGitHubAppManifest({ organization_name: ghOrgName.trim() || undefined });
      callbacks.current.onBeforeRedirect?.();
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = res.github_url;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'manifest';
      input.value = JSON.stringify(res.manifest);
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to start GitHub App registration');
      setModalConnecting(false);
    }
  }

  /** How the chosen provider's repositories are read right now, if at all. */
  const connectionKind: GitConnectionKind | null =
    provider === 'github' && githubAppInstalled ? 'github_app' : currentToken ? 'token' : null;

  return {
    provider,
    setProvider,
    providerAccounts,
    providerTokens,
    connectedProviders,
    githubAppInstalled,
    connectionKind,
    isConnected: connectedProviders[provider],
    repositories,
    loadingRepos,
    repoListError,
    reload,
    // connect dialog
    connectModalOpen,
    setConnectModalOpen,
    modalProvider,
    modalAccount,
    setModalAccount,
    modalToken,
    setModalToken,
    modalConnecting,
    modalError,
    setModalError,
    githubConnectTab,
    setGithubConnectTab,
    ghAppId,
    setGhAppId,
    ghAppName,
    setGhAppName,
    ghClientId,
    setGhClientId,
    ghClientSecret,
    setGhClientSecret,
    ghWebhookSecret,
    setGhWebhookSecret,
    ghPrivateKey,
    setGhPrivateKey,
    ghInstallationId,
    setGhInstallationId,
    ghAccount,
    setGhAccount,
    ghOrgName,
    setGhOrgName,
    handleConnectProvider,
    handleSaveProviderConnection,
    handleSaveManualGitHubApp,
    handleStartManifestFlowFromModal,
  };
}

export type GitRepositories = ReturnType<typeof useGitRepositories>;
